import type { CommonData } from '@/components/blockEditor/blockEditor.types';
import { CURRENT_SCHEMA_VERSION } from '@/components/blockEditor/blockEditor.types';

export function getDefaultCrtOverviewPageOneData(): CommonData {
  return {
    version: CURRENT_SCHEMA_VERSION,
    blocks: [
      {
        id: 'description',
        type: 'ListItemsSection',
        props: {},
        children: {
          default: [
            {
              id: 'crt-overview-heading',
              type: 'EducationalSlideHeading',
              props: {
                title: 'What is a CRT?',
                variant: 'h3',
              },
            },
            {
              id: 'crt-overview-body',
              type: 'EditableEducationalSlideBody',
              props: {
                text: "A Charitable Remainder Trust (CRT) is an irrevocable trust designed to generate a steady income stream for the donor while making a significant philanthropic contribution. CRTs can reduce and defer capital gains taxes on appreciated assets, help the donor achieve income tax deductions, and reduce estate taxes.\n\nThe donor funds the CRT with assets and receives income payments for a specified term (up to 20 years) or for life. After this period, the remaining trust assets are transferred to one or more designated charitable organizations. There are two main payout options for CRTs: Charitable Remainder Annuity Trusts (CRATs), which pay a fixed amount annually, and Charitable Remainder Unitrusts (CRUTs), which pay a fixed percentage of the trust's value, revalued annually.",
              },
            },
          ],
        },
      },
      {
        id: 'benefits',
        type: 'ListItemsSection',
        props: {},
        children: {
          default: [
            {
              id: 'crt-overview-page-one-columns',
              type: 'TwoColumnLayout',
              props: {
                leftWidth: '50%',
                rightWidth: '50%',
                columnGap: 2,
                showDivider: true,
              },
              children: {
                leftColumn: [
                  {
                    id: 'crt-overview-benefits-heading',
                    type: 'EducationalSlideHeading',
                    props: {
                      title: 'Potential benefits',
                      variant: 'h2',
                    },
                  },
                  {
                    id: 'crt-benefit-deduction',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Charitable income tax deduction',
                      description:
                        'Upon establishing the CRT, the donor receives an income tax deduction based on the present value of the future gift to charity.',
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                  {
                    id: 'crt-benefit-gains',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Capital gains tax reduction or deferral',
                      description:
                        'Appreciated assets transferred into the CRT do not trigger capital gains tax upon transfer and sale. This means that the trust assets grow unencumbered by taxes, benefiting both the income and charitable beneficiaries.',
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                  {
                    id: 'crt-benefit-income',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Income stream for donor or beneficiaries',
                      description:
                        "The donor (or other individuals) receives a fixed income or a percentage of the trust's value annually for the term of the trust. This provides a reliable source of income, often after the sale of appreciated assets.",
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                  {
                    id: 'crt-benefit-estate',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Estate tax reduction',
                      description:
                        'CRTs reduce the size of the taxable estate, which can reduce future estate tax liability.',
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                  {
                    id: 'crt-benefit-charity',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Support for charitable causes',
                      description:
                        'At the end of the trust term, the remaining trust assets pass to one or more qualified charities.',
                      iconType: 'check',
                      iconColor: 'PRIMARY',
                    },
                  },
                ],
                rightColumn: [
                  {
                    id: 'crt-overview-drawbacks-heading',
                    type: 'EducationalSlideHeading',
                    props: {
                      title: 'Potential drawbacks',
                      variant: 'h2',
                    },
                  },
                  {
                    id: 'crt-drawback-irrevocability',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Irrevocability',
                      description:
                        'Once established, a CRT cannot be modified or revoked. The charitable remainder is irrevocably committed to charity, and the donor cannot reclaim these assets.',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'crt-drawback-income-variability',
                    type: 'PresentationListItem',
                    props: {
                      title: 'CRUT income variability',
                      description:
                        "Annual payments from a CRUT can fluctuate based on the trust's asset performance, creating potential income instability.",
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'crt-drawback-beneficiary-tax',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Beneficiary taxation',
                      description:
                        'While the trust avoids immediate capital gains tax, the income beneficiaries pay tax on distributions. The taxation methodology may result in distributions being taxed as ordinary income, capital gains, or other income, limiting the net cash flow benefit from the trust.',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                  {
                    id: 'crt-drawback-complexity',
                    type: 'PresentationListItem',
                    props: {
                      title: 'Complexity & costs',
                      description:
                        'Creating and maintaining a CRT involves legal and administrative expenses, and the structure may require ongoing professional management.',
                      iconType: 'flag',
                      iconColor: 'NEGATIVE',
                    },
                  },
                ],
              },
            },
          ],
        },
      },
    ],
  };
}
